import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { FiClipboard, FiSearch } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { getStoredChampId, useChampionships } from '../utilities/championships';
import { champFields, extractEntity, extractList } from '../utilities/tournamentsApi';
import { capApplicationId, formatCapDate, isCapApplicationFromBot } from '../utilities/applicationsApi';
import { getTeamPhotoUrl } from '../utilities/playersApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/applicationsManagement.css';

const CAP_FILTERS_KEY = 'capApplicationsFiltersByChamp';
const CAP_FILTERS_LEGACY_KEY = 'capApplicationsLeagueByChamp';
const DEFAULT_CAP_FILTERS = { leagueId: '', onlyNew: true };

try {
  if (!localStorage.getItem(CAP_FILTERS_KEY)) {
    const legacy = localStorage.getItem(CAP_FILTERS_LEGACY_KEY);
    if (legacy) localStorage.setItem(CAP_FILTERS_KEY, legacy);
  }
} catch (_error) {
  /* ignore */
}

const readStoredCapFilters = (champId) => {
  const champ = Number(champId);
  if (!Number.isFinite(champ) || champ <= 0) return { ...DEFAULT_CAP_FILTERS };
  try {
    const raw = JSON.parse(localStorage.getItem(CAP_FILTERS_KEY) || '{}');
    const value = raw?.[String(champ)];
    // legacy: только id лиги строкой
    if (typeof value === 'string' || typeof value === 'number') {
      return {
        leagueId: value ? String(value) : '',
        onlyNew: true,
      };
    }
    if (value && typeof value === 'object') {
      return {
        leagueId: value.leagueId ? String(value.leagueId) : '',
        onlyNew: value.onlyNew !== false,
      };
    }
  } catch (_error) {
    /* ignore */
  }
  return { ...DEFAULT_CAP_FILTERS };
};

const writeStoredCapFilters = (champId, { leagueId, onlyNew }) => {
  const champ = Number(champId);
  if (!Number.isFinite(champ) || champ <= 0) return;
  try {
    const raw = JSON.parse(localStorage.getItem(CAP_FILTERS_KEY) || '{}');
    raw[String(champ)] = {
      leagueId: leagueId ? String(leagueId) : '',
      onlyNew: onlyNew !== false,
    };
    localStorage.setItem(CAP_FILTERS_KEY, JSON.stringify(raw));
  } catch (_error) {
    /* ignore */
  }
};

const statusClass = (status) => {
  switch (Number(status)) {
    case 1: return 'accepted';
    case 0: return 'declined';
    case 3: return 'partial';
    case 2: return 'pending';
    default: return 'pending';
  }
};

export default function ApplicationsManagment() {
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();

  const [applications, setApplications] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState(() => readStoredCapFilters(getStoredChampId()).leagueId);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyNew, setOnlyNew] = useState(() => readStoredCapFilters(getStoredChampId()).onlyNew);
  const [loading, setLoading] = useState(true);
  const [leaguesLoading, setLeaguesLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const searchTimer = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!champId) {
      setLeagueId('');
      setOnlyNew(true);
      return;
    }
    const stored = readStoredCapFilters(champId);
    setLeagueId(stored.leagueId);
    setOnlyNew(stored.onlyNew);
  }, [champId]);

  useEffect(() => {
    if (!champId) return;
    writeStoredCapFilters(champId, { leagueId, onlyNew });
  }, [champId, leagueId, onlyNew]);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    loadLeagues();
  }, [champId, champsLoading]);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadApplications(searchQuery);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, champId, champsLoading, onlyNew, leagueId]);

  const leaguePlaceLabel = (item) => (
    [item.country_name, item.league_name].filter(Boolean).join(' · ') || '—'
  );

  const loadLeagues = async () => {
    if (!champId) {
      setLeagues([]);
      setLeagueId('');
      return;
    }
    setLeaguesLoading(true);
    try {
      const countriesResponse = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      const countries = extractList(countriesResponse.data);
      const nested = await Promise.all(countries.map(async (country) => {
        try {
          const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(country.id) });
          return extractList(extractEntity(response.data).leagues).map((league) => ({
            id: Number(league.id || league.league_id),
            name: league.name || league.league_name || '—',
            country_id: Number(country.id),
            country_name: country.name || country.country_name || '',
          }));
        } catch (error) {
          return [];
        }
      }));
      const next = nested
        .flat()
        .filter((item) => Number.isFinite(item.id) && item.id > 0)
        .sort((left, right) => {
          const byCountry = String(left.country_name || '').localeCompare(String(right.country_name || ''), 'ru', { sensitivity: 'base' });
          if (byCountry) return byCountry;
          return String(left.name || '').localeCompare(String(right.name || ''), 'ru', { sensitivity: 'base' });
        });
      setLeagues(next);
      setLeagueId((prev) => (
        prev && next.some((item) => String(item.id) === String(prev)) ? prev : ''
      ));
    } catch (error) {
      setLeagues([]);
      setLeagueId('');
    } finally {
      setLeaguesLoading(false);
    }
  };

  const loadApplications = async (search) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const body = { ...champFields(champId) };
      if (onlyNew) body.only_new = true;
      if (leagueId) body.league_id = Number(leagueId);
      const query = String(search || '').trim();
      if (query) body.search = query;
      const response = await authorizedPost(dispatch, API_URLS.getAdminCapApplications, body);
      if (requestId !== requestIdRef.current) return;
      setApplications(extractList(response.data));
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setApplications([]);
      setMessage({ text: getApiErrorMessage(error, t('capLoadError')), type: 'error' });
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const statusLabel = (status) => {
    switch (Number(status)) {
      case 1: return t('capStatusAccepted');
      case 0: return t('capStatusDeclined');
      case 3: return t('capStatusPartial');
      case 2: return t('capStatusPending');
      default: return '—';
    }
  };

  const leagueGroups = useMemo(() => {
    const groups = [];
    const indexByCountry = new Map();
    leagues.forEach((league) => {
      const key = String(league.country_id || league.country_name || '');
      if (!indexByCountry.has(key)) {
        indexByCountry.set(key, groups.length);
        groups.push({
          key,
          label: league.country_name || t('trCountry'),
          items: [],
        });
      }
      groups[indexByCountry.get(key)].items.push(league);
    });
    return groups;
  }, [leagues, t]);

  const columns = [
    {
      key: 'team',
      label: t('capTeam'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="tr-name cap-app-main">
          <img
            src={getTeamPhotoUrl(item.team_image) || defaultTeamLogo}
            alt=""
            className="tr-team-logo"
            onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
          />
          <span className="cap-app-text">
            <span className="cap-app-team-row">
              <span className="cap-app-team">{item.team_name || '—'}</span>
              {isCapApplicationFromBot(item) ? (
                <span className="cap-source-badge" title={t('capFromBot')}>{t('capFromBot')}</span>
              ) : null}
            </span>
            <small className="cap-app-meta">
              {[
                leaguePlaceLabel(item),
                item.application_name,
                formatCapDate(item.created_at, lang),
              ].filter((value) => value && value !== '—').join(' · ') || '—'}
            </small>
          </span>
        </span>
      ),
    },
    {
      key: 'league',
      label: t('capPlace'),
      className: 'st-cell-muted cap-cell-place',
      render: (item) => (
        <span className="cap-place">
          <span className="cap-place-country">{item.country_name || '—'}</span>
          <small className="cap-place-league">{item.league_name || '—'}</small>
        </span>
      ),
    },
    {
      key: 'application_name',
      label: t('capName'),
      className: 'st-cell-muted cap-cell-app',
      render: (item) => item.application_name || '—',
    },
    {
      key: 'created_at',
      label: t('capDate'),
      className: 'st-cell-muted cap-cell-date',
      render: (item) => formatCapDate(item.created_at, lang),
    },
    {
      key: 'status',
      label: t('capStatus'),
      className: 'st-cell-status',
      render: (item) => (
        <span
          className={`st-badge cap-badge ${statusClass(item.status)}`}
          title={statusLabel(item.status)}
          aria-label={statusLabel(item.status)}
        >
          <span className="cap-badge-label">{statusLabel(item.status)}</span>
        </span>
      ),
    },
  ];

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page cap-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navApplications') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="cap-toolbar-row">
            <div className="st-search">
              <FiSearch className="st-search-icon" />
              <input
                type="text"
                className="st-search-input"
                placeholder={t('capSearchPh')}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              {searchQuery ? (
                <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                  ×
                </button>
              ) : null}
            </div>
            <select
              className="st-select cap-league-select"
              value={leagueId}
              disabled={leaguesLoading || !champId}
              onChange={(event) => setLeagueId(event.target.value)}
              aria-label={t('capLeague')}
            >
              <option value="">{t('capAllLeagues')}</option>
              {leagueGroups.map((group) => (
                <optgroup key={group.key} label={group.label}>
                  {group.items.map((league) => (
                    <option key={league.id} value={String(league.id)}>
                      {league.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <label className="st-check">
              <input
                type="checkbox"
                checked={onlyNew}
                onChange={() => setOnlyNew((value) => !value)}
              />
              {t('capOnlyNew')}
            </label>
            <div className="st-meta">
              <strong>{t('stFound', { count: applications.length })}</strong>
            </div>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            <div className="st-block">
              <h2 className="st-block-header">{t('navApplications')}</h2>
              {loading ? (
                <div className="st-loading">
                  <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                  <p>{t('capLoading')}</p>
                </div>
              ) : (
                <ReorderableTable
                  tableClassName="st-table-cap-apps"
                  columns={columns}
                  items={applications}
                  getRowKey={(item) => capApplicationId(item) || item.team_id}
                  reorderMode={false}
                  getRowHref={(item) => {
                    const id = capApplicationId(item);
                    return id ? `/manage_application/${id}` : null;
                  }}
                  empty={(
                    <div className="st-empty">
                      <FiClipboard size={48} />
                      <h3>{t('capEmpty')}</h3>
                      <p>{t('capEmptyHint')}</p>
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
